import React from "react";
import { Link } from "react-router-dom";
import LegalPage, { P, Section, List, Mail } from "../../components/LegalPage";
import DeleteAccountSection from "../../components/DeleteAccountSection";
import { useAuth } from "../../context/AuthContext";
import { COMPANY, SUPPORT_EMAIL, DELETE_ACCOUNT_UPDATED } from "../../data/legalInfo";

// Google Play requires a web page, outside the app, where a user can request
// deletion of their account: someone who has uninstalled Lineup cannot reach
// the in-app flow. The URL is submitted in the Data safety form, and the policy
// asks for three things, all of which this page has to keep:
//
//   1. It loads without an account. This route sits outside PrivateRoute.
//   2. The deletion pathway is prominently featured, so it comes first, above
//      the explanation rather than below it.
//   3. It names the app as it appears on the store listing, and the developer.
//
// https://support.google.com/googleplay/android-developer/answer/13327111
//
// The page renders bare in tests, with no AuthProvider above it, so useAuth()
// can come back undefined. Signed out is the correct reading of that.

const link = { color: "var(--color-brand)", textDecoration: "none" };

export default function DeleteAccount() {
  const { currentUser } = useAuth() || {};

  return (
    <LegalPage
      title="Delete your Lineup account"
      subtitle={`Last updated ${DELETE_ACCOUNT_UPDATED}`}
    >
      <P>
        Lineup is a fantasy college football game operated by {COMPANY}. You can delete your Lineup
        account at any time, from this page or from account settings inside the app. Deletion is
        permanent and cannot be undone.
      </P>

      <Section title="Delete your account">
        {currentUser ? (
          <>
            <P>
              You are signed in. Use the button below. You will be asked to confirm before anything
              is deleted.
            </P>
            <DeleteAccountSection prominent />
          </>
        ) : (
          <>
            <P>
              You are not signed in. Deletion has to come from the account itself, so we can be sure
              the request is yours and not someone else's. Sign in first, then return to this page
              or open account settings in the app and delete from there.
            </P>
            <Link
              to="/login"
              style={{
                display: "block",
                textAlign: "center",
                padding: "14px 0",
                borderRadius: 10,
                backgroundColor: "var(--color-cta)",
                color: "var(--color-cta-text)",
                fontWeight: 700,
                fontSize: 15,
                textDecoration: "none",
                marginBottom: 16,
              }}
            >
              Log in to delete your account
            </Link>
            <P>
              If you cannot sign in, email <Mail address={SUPPORT_EMAIL} /> from the address on the
              account and ask us to delete it. We will confirm the request with you and complete it
              within 30 days.
            </P>
          </>
        )}
      </Section>

      <Section title="What deletion removes">
        <P>
          Your sign in credentials are deleted outright. That is the password, the email address
          held by our authentication provider, and every active session and refresh token. Once that
          record is gone the account cannot be signed into or recovered.
        </P>
        <P>These personal details are erased from your Lineup profile:</P>
        <List
          items={[
            "Your first and last name.",
            "Your email address.",
            "Your date of birth.",
            "Your profile image and avatar.",
            "Your league smack talk.",
            "Your push notification token, so notifications stop.",
          ]}
        />
      </Section>

      <Section title="What stays, and why">
        <P>
          Your Lineup profile row is not deleted outright. It is emptied of everything above and
          left in place under the placeholder name "Former Manager", marked as deleted.
        </P>
        <P>
          This is deliberate. Your league membership, your draft picks, your weekly lineups, your
          roster moves and your points are all attached to that record, and they are also part of
          every other manager's league history. Deleting the record would either be refused by the
          database or would take their season with it. Emptying it removes you while leaving their
          standings, draft board and results intact.
        </P>
        <P>
          So a former leaguemate looking at an old draft board or a past standings table will see
          "Former Manager" where your name used to be, with the picks and points that were made
          under it. They will not see your name, your email, your date of birth or your profile
          image, because those no longer exist.
        </P>
      </Section>

      <Section title="How long it takes">
        <P>
          Deletion from the live service is immediate. Copies of your information can survive for a
          short time in our hosting provider's encrypted backups, until those backups age out on
          that provider's retention schedule. A deleted account is never restored from a backup.
          Server logs kept by our hosting providers for security and troubleshooting, which may
          include an IP address and request timestamps, expire on those providers' own retention
          schedules.
        </P>
      </Section>

      <Section title="Questions">
        <P>
          Email <Mail address={SUPPORT_EMAIL} /> if you want to check what is held on your account
          before deleting it, or if something on this page is unclear. Our{" "}
          <Link to="/privacy" style={link}>
            privacy policy
          </Link>{" "}
          covers what we collect and how long we keep it.
        </P>
      </Section>
    </LegalPage>
  );
}
